import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import useTelegram from "../hooks/telegram-hook";
import "../styles/mining/main.css";
import "../styles/mining/modals/main.css";
import { lockSvg } from "img/mining/lock";
import { addSvg } from "img/mining/add";
import UserApi from "../services/api-service";

// ---------- Типы данных ----------
interface VideoCardData {
  Id: number;
  Lvl: number;
  Balance: number;
  income: number;
  Fuel: number;
}

interface SlotData {
  id: number;
  gpuId: number | null;
}

type ActiveModal =
  | "selectAction"
  | "changeGpu"
  | "coolGpu"
  | "buySlot"
  | "installGpu"
  | null;

// ---------- Основной компонент ----------
function Mining(): JSX.Element {
  const location = useLocation();
  const telegram = useTelegram();
  const { id: userId = "1938421476" } = telegram?.user || {};

  // ------------------ Состояния ------------------
  const [btcBalance, setBtcBalance] = useState(0);
  const [balance, setBalance] = useState(0);
  const [coolingLiquid, setCoolingLiquid] = useState(0);

  const [slots, setSlots] = useState<SlotData[]>([]);
  const [videoCards, setVideoCards] = useState<Record<number, VideoCardData>>(
    {}
  );

  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);

  // const [isLoading, setIsLoading] = useState(true);
  const [slotPrice, setSlotPrice] = useState<string | null>(null);
  const [buySlotMessage, setBuySlotMessage] = useState("");
  const [selectedGpuIdToInstall, setSelectedGpuIdToInstall] = useState<
    number | null
  >(null);
  const [statusMessage, setStatusMessage] = useState("");

  // ------------------ Утилита форматирования GPU ------------------
  const formatGpus = (allGpus: any[]): Record<number, VideoCardData> => {
    return allGpus.reduce((acc, { card, income }) => {
      acc[card.Id] = {
        Id: card.Id,
        Lvl: card.Lvl,
        Balance: card.Balance,
        Fuel: card.Fuel,
        income,
      };
      return acc;
    }, {} as Record<number, VideoCardData>);
  };

  // общий хендлер закрытия модалки
  const handleCloseModal = () => {
    setActiveModal(null);
    setStatusMessage(""); // сбрасываем сообщение при закрытии
  };

  // ------------------ Обновление данных ------------------
  const updateUserData = useCallback(async () => {
    try {
      // setIsLoading(true);
      const { Balance, Coin, Freeze } = await UserApi.getUser(userId);
      const rawSlots = await UserApi.miningGetSlots(userId);
      const allGpus = await UserApi.miningGetGpu(userId);

      setBalance(Balance);
      setBtcBalance(Number(Coin) || 0);
      setCoolingLiquid(Number(Freeze) || 0);

      setSlots(
        (rawSlots || []).map((slot: any) => ({
          id: slot.id,
          gpuId: slot.card?.Id ?? null,
        }))
      );
      setVideoCards(formatGpus(allGpus));
    } catch (err) {
      console.error("Ошибка при загрузке данных:", err);
    } finally {
      // setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    updateUserData();
  }, [updateUserData]);

  // ------------------ Действия ------------------
  const handleOpenActionModal = (cardId: number) => {
    setSelectedCardId(cardId);
    setActiveModal("selectAction");
  };

  const handleOpenBuySlotModal = async () => {
    try {
      const price = await UserApi.miningSlotPrice();
      setSlotPrice(price);
      setBuySlotMessage("");
      setActiveModal("buySlot");
    } catch (err) {
      console.error("Ошибка получения цены слота:", err);
    }
  };

  const handleBuySlot = async () => {
    if (!userId) return;
    try {
      handleCloseModal();
      const result = await UserApi.miningBuySlot(userId);

      if (result?.status === "success") {
        await updateUserData();
      } else {
        const errorMessages: Record<string, string> = {
          noBalance: "❌ Недостаточно средств для покупки.",
          maxSlots: "⚠️ Ты достиг максимального количества слотов.",
        };
        setBuySlotMessage(
          errorMessages[result?.status] || "Ошибка при покупке."
        );
        setActiveModal("buySlot");
      }
    } catch (err) {
      console.error("Ошибка при покупке слота:", err);
      setBuySlotMessage("Произошла ошибка при покупке.");
      setActiveModal("buySlot");
    }
  };

  const handleOpenInstallGpuModal = (slotId: number) => {
    setSelectedCardId(slotId);
    setActiveModal("installGpu");

    // выбираем свободную видеокарту
    const freeGpu = Object.values(videoCards).find(
      (gpu) => !slots.some((slot) => slot.gpuId === gpu.Id)
    );
    setSelectedGpuIdToInstall(freeGpu?.Id ?? null);
  };

  const handleInstallGpu = async () => {
    if (selectedGpuIdToInstall === null || selectedCardId === null) return;

    try {
      const response = await UserApi.miningInstallGpuToSlot(
        userId,
        selectedCardId,
        selectedGpuIdToInstall
      );

      if (response?.status === "success") {
        await updateUserData();
      } else {
        // даже если не success — обновим состояние
        const allGpus = await UserApi.miningGetGpu(userId);
        setVideoCards(formatGpus(allGpus));
      }
      handleCloseModal();
    } catch (err) {
      console.error("Ошибка установки видеокарты:", err);
    }
  };

  const handlePullGpu = async () => {
    if (!userId || selectedCardId === null) return;
    try {
      const result = await UserApi.miningPullGpu(userId, selectedCardId);
      if (result?.status === "success") {
        await updateUserData();
        handleCloseModal();
      } else {
        console.warn("Не удалось извлечь видеокарту:", result);
      }
    } catch (err) {
      console.error("Ошибка при извлечении видеокарты:", err);
    }
  };

  const handlePourGpu = async () => {
    if (!userId || selectedCardId === null) return;

    try {
      const result = await UserApi.miningPourGpu(userId, selectedCardId);

      if (result?.status === "success") {
        setStatusMessage("✅ Жидкость успешно залита!");
        await updateUserData();
        return;
      }

      if (result?.status === "dontFreeze") {
        setStatusMessage("❌ У тебя нет охлаждающей жидкости!");
      }

      if (result?.status === "alreadyFull") {
        setStatusMessage("❌ Уже охлаждена!");
      }
    } catch (err) {
      console.error("Ошибка при заливке жидкости:", err);
      setStatusMessage("Произошла ошибка при заливке жидкости.");
    }
  };

  const handleWithdrawBitcoin = async () => {
    if (!userId || selectedCardId === null) return;
    try {
      const result = await UserApi.withdrawBitcoin(userId, selectedCardId);

      if (result?.status === "success") {
        await updateUserData();
      }

      handleCloseModal();
    } catch (err) {
      console.error("Ошибка при выводе биткоинов:", err);
    }
  };

  // ------------------ Рендер слотов ------------------
  const renderSlots = () => {
    const maxSlots = 9;
    return Array.from({ length: maxSlots }, (_, i) => {
      const slot = slots[i];

      if (!slot) {
        return (
          <article
            className="video-card locked-card"
            key={`locked-${i}`}
            onClick={handleOpenBuySlotModal}
          >
            <span className="locked-icon">
              {lockSvg({ pathname: location.pathname })}
            </span>
            <div className="locked-text">Стойка недоступна</div>
          </article>
        );
      }

      if (slot.gpuId === null) {
        return (
          <article
            className="video-card add-card"
            key={`add-${slot.id}`}
            onClick={() => handleOpenInstallGpuModal(slot.id)}
          >
            <span className="add-icon">
              {addSvg({ pathname: location.pathname })}
            </span>
            <div className="add-text">Добавить видеокарту</div>
          </article>
        );
      }

      const gpu = videoCards[slot.gpuId];
      if (!gpu) return null;

      return (
        <article
          className="video-card"
          key={`gpu-${gpu.Id}`}
          aria-label={`Видеокарта ${gpu.Id}`}
          onClick={() => handleOpenActionModal(gpu.Id)}
        >
          <div className="level-badge">{gpu.Lvl} LVL</div>
          <div className="video-card-info">
            <p>
              <span className="label-muted">Баланс:</span>{" "}
              <b>{gpu.Balance} BTC</b>
            </p>
            <p>
              <span className="label-muted">Доход:</span>{" "}
              <b>{gpu.income} BTC/день</b>
            </p>
            <p className="cooling-label">Охлаждающая жидкость:</p>
            <div className="cooling-bar">
              <div
                className="cooling-progress"
                style={{ width: `${gpu.Fuel}%` }}
              />
              <span className="cooling-percent">{gpu.Fuel}%</span>
            </div>
          </div>
        </article>
      );
    });
  };

  // ------------------ Рендер ------------------
  return (
    <div className="app-root">
      <main className="mining-page">
        <section className="mining-stats">
          <div>
            <span className="label btc-label">BTC</span> <b>{btcBalance}</b>
          </div>
          <div>
            <span className="label">Охлаждающая жидкость</span>{" "}
            <b>{coolingLiquid}</b>
          </div>
          <div>
            <span className="label">Старки</span> <b>{balance}</b>
          </div>
        </section>

        <h2 className="mining-title">Видеокарты</h2>
        <section className="video-cards-grid">{renderSlots()}</section>
      </main>

      {/* --- Модалки --- */}
      {activeModal === "buySlot" && (
        <Modal onClose={() => handleCloseModal()} hideCloseBtn>
          <p className="buySlot__h3">
            💡 Стоимость стойки:
            <br />
            <b>{slotPrice} старок</b>
          </p>
          {buySlotMessage && (
            <p className="buySlot__message">{buySlotMessage}</p>
          )}
          <div className="modal-actions">
            <button onClick={() => handleCloseModal()}>Отмена</button>
            <button onClick={handleBuySlot}>Купить</button>
          </div>
        </Modal>
      )}

      {activeModal === "installGpu" && (
        <Modal onClose={() => handleCloseModal()} hideCloseBtn>
          <h3>Выбери видеокарту</h3>
          <select
            value={selectedGpuIdToInstall || ""}
            onChange={(e) => setSelectedGpuIdToInstall(Number(e.target.value))}
          >
            {Object.values(videoCards).map((gpu) => {
              const isInstalled = slots.some((slot) => slot.gpuId === gpu.Id);
              return (
                <option key={gpu.Id} value={gpu.Id} disabled={isInstalled}>
                  Видеокарта[{gpu.Id}] {gpu.Lvl} LVL
                </option>
              );
            })}
          </select>
          <div className="modal-actions">
            <button onClick={() => handleCloseModal()}>Отмена</button>
            <button onClick={handleInstallGpu}>Установить</button>
          </div>
        </Modal>
      )}

      {activeModal === "selectAction" && (
        <Modal onClose={() => handleCloseModal()}>
          <h3>Выбери действие</h3>
          <button onClick={() => setActiveModal("coolGpu")}>
            Залить охлаждающую жидкость
          </button>
          <button onClick={handlePullGpu}>Вытащить видеокарту</button>
          <button onClick={handleWithdrawBitcoin}>Снять биткоины</button>
        </Modal>
      )}

      {activeModal === "coolGpu" && (
        <Modal onClose={() => handleCloseModal()}>
          <h3>Охлаждение видеокарты #{selectedCardId}</h3>
          <p>
            У тебя <b>{coolingLiquid}</b> охлаждающих жидкостей
          </p>
          {statusMessage && <p className="status-message">{statusMessage}</p>}
          <div className="modal-actions">
            <button onClick={handlePourGpu}>Залить</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------- Модалка ----------
interface ModalProps {
  children: React.ReactNode;
  onClose: () => void;
  hideCloseBtn?: boolean;
}

function Modal({ children, onClose, hideCloseBtn }: ModalProps) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {children}
        {!hideCloseBtn && (
          <button className="close-btn" onClick={onClose}>
            Закрыть
          </button>
        )}
      </div>
    </div>
  );
}

export default Mining;
